import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { getQuestionID } from '../../globals/application';
import ContentNetworkLayer from '../../helpers';
import AddSubtopicQuestion from '../AddSubtopicQuestion';
import DiagramModal from '../DiagramModal';
import { subjectUsesMath } from '../EquationModal';
import CustomModal from '../Modal';
import NotePreview from '../NotePreview';
import RichEditor from '../RichEditor';

export default function AddNotes() {
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [subTopics, setSubTopics] = useState([]);
  const [subject, setSubject] = useState('');
  const [topic, setTopic] = useState('');
  const [subTopic, setSubTopic] = useState('');
  const [notes, setNotes] = useState('');
  const [facts, setFacts] = useState('');
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [imagePosition, setImagePosition] = useState('before'); // 'before' or 'after'
  const [loading, setLoading] = useState(false);
  const [showLocationWarning, setShowLocationWarning] = useState(false);
  const [showDiagramModal, setShowDiagramModal] = useState(false);

  // Steps state - array of { step_number, title, content }
  const [steps, setSteps] = useState([]);
  const [showSteps, setShowSteps] = useState(false);

  const question_id = useSelector(getQuestionID);

  // Check if location is fully selected
  const hasLocation = subject && topic && subTopic;

  // Steps handlers
  const addStep = () => {
    setSteps([
      ...steps,
      {
        step_number: steps.length + 1,
        title: '',
        content: '',
      },
    ]);
  };

  const updateStep = (index, field, value) => {
    const updated = [...steps];
    updated[index][field] = value;
    setSteps(updated);
  };

  const removeStep = index => {
    const updated = steps.filter((_, i) => i !== index);
    // Re-number steps
    updated.forEach((step, i) => (step.step_number = i + 1));
    setSteps(updated);
  };

  const moveStep = (index, direction) => {
    if ((direction === -1 && index === 0) || (direction === 1 && index === steps.length - 1))
      return;

    const updated = [...steps];
    const temp = updated[index];
    updated[index] = updated[index + direction];
    updated[index + direction] = temp;
    // Re-number steps
    updated.forEach((step, i) => (step.step_number = i + 1));
    setSteps(updated);
  };

  const filteredTopics = topics.filter(t => t.code === parseInt(subject));
  const filteredSubTopics = subTopics.filter(st => st.topicId === parseInt(topic));

  // Check if selected subject uses math symbols
  const selectedSubject = useMemo(
    () => subjects.find(s => s.code === parseInt(subject)),
    [subjects, subject]
  );
  const showEquationButton = useMemo(
    () => subjectUsesMath(selectedSubject?.name),
    [selectedSubject]
  );

  // Valid if: subTopic selected AND (notes has content OR steps have content)
  const hasNotes = notes.replace(/<[^>]*>/g, '').trim();
  const hasSteps = steps.length > 0 && steps.some(s => s.title.trim() || s.content.trim());
  const isValid = subTopic && (hasNotes || hasSteps);

  const handleFile = file => {
    setFileName(file.name);
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => setFile(reader.result);
    reader.onerror = () => toast.error('Could not read file.');
  };

  useEffect(() => {
    Promise.all([
      ContentNetworkLayer.getSubjects(),
      ContentNetworkLayer.getTopics(),
      ContentNetworkLayer.getSubTopics(),
    ])
      .then(([subRes, topRes, stRes]) => {
        setSubjects(subRes.subjects);
        setTopics(topRes.topics);
        setSubTopics(stRes.subTopics);
      })
      .catch(() => toast.error('Could not load data.'));
  }, []);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!isValid) return;
    setLoading(true);
    try {
      const payload = {
        subtopic_id: parseInt(subTopic),
        notes,
        ...(facts.replace(/<[^>]*>/g, '').trim() && { facts }),
        ...(question_id && { question_id: parseInt(question_id) }),
        // Include steps if any have content
        ...(steps.length > 0 &&
          steps.some(s => s.title.trim() || s.content.trim()) && {
            steps: JSON.stringify(steps.filter(s => s.title.trim() || s.content.trim())),
          }),
      };
      if (file) {
        const file_id = Math.floor(100000 + Math.random() * 900000);
        payload.file = file;
        payload.file_id = file_id;
        payload.filename = `note-${subTopic}-${file_id}`;
        payload.image_position = imagePosition;
      }
      await ContentNetworkLayer.createNotes(payload);
      toast.success('Notes saved!');
      setNotes('');
      setFacts('');
      setFile(null);
      setFileName('');
      setImagePosition('before');
      setSubTopic('');
      setSteps([]);
      setShowSteps(false);
    } catch (error) {
      toast.error('Failed to save notes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="notes-layout">
      {/* Left side - Form */}
      <div className="form-card">
        <div className="form-card-header">
          <div style={{ flex: 1 }}>
            <h2>Add Notes</h2>
            <p>Write educational notes and facts for a sub-topic.</p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Location cascade */}
          <div className="field-group">
            <label className="field-label">Subject</label>
            <select
              className="field-select"
              value={subject}
              onChange={e => {
                setSubject(e.target.value);
                setTopic('');
                setSubTopic('');
              }}
            >
              <option value="">Select a subject...</option>
              {subjects.map(s => (
                <option key={s.code} value={s.code}>
                  {s.name} — {s.level}
                </option>
              ))}
            </select>
          </div>

          <div className="field-row">
            <div className="field-group">
              <label className="field-label">Topic</label>
              <select
                className="field-select"
                value={topic}
                onChange={e => {
                  setTopic(e.target.value);
                  setSubTopic('');
                }}
                disabled={!subject}
              >
                <option value="">{subject ? 'Select a topic...' : 'Select subject first'}</option>
                {filteredTopics.map(t => (
                  <option key={t.topicId} value={t.topicId}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="field-group">
              <label className="field-label">Sub-Topic</label>
              <select
                className="field-select"
                value={subTopic}
                onChange={e => setSubTopic(e.target.value)}
                disabled={!topic}
              >
                <option value="">{topic ? 'Select a sub-topic...' : 'Select topic first'}</option>
                {filteredSubTopics.map(st => (
                  <option key={st.subTopicId} value={st.subTopicId}>
                    {st.title}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="divider" />

          {/* Content */}
          <div className="field-group">
            <label className="field-label">Notes</label>
            <RichEditor
              value={notes}
              onChange={setNotes}
              placeholder="Write your notes here..."
              minHeight={140}
              showEquationButton={showEquationButton}
            />
          </div>

          <div className="field-group">
            <label className="field-label">Facts / Sub-text</label>
            <RichEditor
              value={facts}
              onChange={setFacts}
              placeholder="Key facts, formulas, or additional context..."
              minHeight={100}
              showEquationButton={showEquationButton}
            />
          </div>

          {/* Steps Section */}
          <div className="field-group">
            <div className="steps-header">
              <label className="field-label">Step-by-Step Instructions (optional)</label>
              {!showSteps && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    setShowSteps(true);
                    if (steps.length === 0) addStep();
                  }}
                >
                  + Add Steps
                </button>
              )}
            </div>

            {showSteps && (
              <div className="steps-container">
                {steps.map((step, index) => (
                  <div key={index} className="step-card">
                    <div className="step-card-header">
                      <span className="step-number-badge">Step {step.step_number}</span>
                      <div className="step-actions">
                        <button
                          type="button"
                          className="step-move-btn"
                          onClick={() => moveStep(index, -1)}
                          disabled={index === 0}
                          title="Move up"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="step-move-btn"
                          onClick={() => moveStep(index, 1)}
                          disabled={index === steps.length - 1}
                          title="Move down"
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          className="remove-answer-btn"
                          onClick={() => removeStep(index)}
                          title="Remove step"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                    <div className="step-fields">
                      <input
                        type="text"
                        className="field-input step-title-input"
                        placeholder="Step title (e.g., 'Calculate the derivative')"
                        value={step.title}
                        onChange={e => updateStep(index, 'title', e.target.value)}
                      />
                      <RichEditor
                        value={step.content}
                        onChange={val => updateStep(index, 'content', val)}
                        placeholder="Detailed explanation for this step..."
                        minHeight={80}
                        showEquationButton={showEquationButton}
                      />
                    </div>
                  </div>
                ))}
                <button type="button" className="btn btn-secondary btn-sm" onClick={addStep}>
                  + Add Another Step
                </button>
              </div>
            )}
          </div>

          {/* File upload */}
          <div className="field-group">
            <label className="field-label">Attachment (optional)</label>
            <div
              className="upload-area"
              onDragOver={e => {
                e.preventDefault();
                e.currentTarget.classList.add('upload-area--drag');
              }}
              onDragLeave={e => e.currentTarget.classList.remove('upload-area--drag')}
              onDrop={e => {
                e.preventDefault();
                e.currentTarget.classList.remove('upload-area--drag');
                const dropped = e.dataTransfer.files && e.dataTransfer.files[0];
                if (dropped) handleFile(dropped);
              }}
            >
              <input
                type="file"
                id="notes-file"
                hidden
                accept="image/*,application/pdf"
                onChange={e => handleFile(e.target.files[0])}
                onClick={e => (e.target.value = null)}
              />
              <label htmlFor="notes-file" className="upload-btn">
                <span>+</span> Choose file
              </label>
              <button
                type="button"
                className="upload-btn"
                onClick={() => setShowDiagramModal(true)}
              >
                <span>✏️</span> Draw Diagram
              </button>
              <span className="upload-dnd-hint">or drag &amp; drop here</span>
              {fileName && <span className="upload-filename">{fileName}</span>}
            </div>

            {/* Image position selector - only show when file is selected */}
            {fileName && (
              <div className="image-position-selector">
                <label className="field-label-sm">Image Position:</label>
                <div className="radio-group">
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="imagePosition"
                      value="before"
                      checked={imagePosition === 'before'}
                      onChange={e => setImagePosition(e.target.value)}
                    />
                    <span>Before notes</span>
                  </label>
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="imagePosition"
                      value="after"
                      checked={imagePosition === 'after'}
                      onChange={e => setImagePosition(e.target.value)}
                    />
                    <span>After notes</span>
                  </label>
                </div>
              </div>
            )}
          </div>

          <div className="form-footer">
            {/* Attach Question - show warning if location not selected */}
            {hasLocation ? (
              <CustomModal
                buttonLabel="+ Attach Question"
                title="Add Subtopic Question"
                content={
                  <AddSubtopicQuestion
                    initialSubject={subject}
                    initialTopic={topic}
                    initialSubTopic={subTopic}
                    lockLocation={true}
                  />
                }
              />
            ) : (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setShowLocationWarning(true)}
              >
                + Attach Question
              </button>
            )}
            <div className="form-footer-actions">
              <button type="submit" className="btn btn-primary" disabled={!isValid || loading}>
                {loading ? 'Saving...' : 'Save Notes'}
              </button>
            </div>
          </div>

          {/* Location Warning Modal */}
          {showLocationWarning && (
            <div className="modal-overlay" onClick={() => setShowLocationWarning(false)}>
              <div className="modal-box modal-sm" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>Select Location First</h3>
                  <button className="modal-close" onClick={() => setShowLocationWarning(false)}>
                    &#x2715;
                  </button>
                </div>
                <div className="modal-body">
                  <div className="warning-content">
                    <span className="warning-icon">⚠️</span>
                    <p>
                      Please select a <strong>Subject</strong>, <strong>Topic</strong>, and{' '}
                      <strong>Sub-Topic</strong> before attaching a question.
                    </p>
                  </div>
                  <div className="modal-actions">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => setShowLocationWarning(false)}
                    >
                      Got it
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </form>

        <DiagramModal
          isOpen={showDiagramModal}
          onClose={() => setShowDiagramModal(false)}
          onInsert={({ dataUrl, name }) => {
            setFile(dataUrl);
            setFileName(name);
          }}
        />
      </div>

      {/* Right side - Preview */}
      <div className="preview-panel">
        <NotePreview
          content={notes}
          facts={facts}
          steps={showSteps ? steps : []}
          image={file}
          imagePosition={imagePosition}
        />
      </div>
    </div>
  );
}
